import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Read tenant timezone from middleware-inlined config. When set
 * (e.g. "Asia/Karachi"), formatDate / formatTime / formatDateTime
 * render in that zone instead of the browser's local zone — so an
 * order placed at 11pm Karachi time looks like 11pm to the vendor
 * even if their device clock is in Dubai.
 *
 * Returns `undefined` when no tenant timezone is set OR the value is
 * not a valid IANA zone id (`Intl.DateTimeFormat` throws RangeError).
 * Callers pass `undefined` to dayjs.tz()/Intl, which means "use
 * browser local" — same as pre-feature behavior.
 */
function getTenantTimezone(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const tz = (window as unknown as {
    __TENANT_CONFIG__?: { branding?: { localization?: { timezone?: unknown } } }
  }).__TENANT_CONFIG__?.branding?.localization?.timezone
  if (typeof tz !== 'string' || !tz.trim()) return undefined
  // Cheap validation — Intl throws on bogus zones, but we only catch
  // once at module load. Cache the verdict to avoid try/catch in hot
  // path.
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date())
    return tz
  } catch {
    return undefined
  }
}

// Platform-driven currency configuration. The boot flow is:
//   1. `initCurrencyConfig()` — synchronous, runs in main.tsx before render.
//      Seeds `_currencyCode` / `_currencyLocale` from the tenant config
//      already inlined into `window.__TENANT_CONFIG__.branding.localization`
//      so the first paint shows the correct currency even on cold start.
//   2. `refreshCurrencyConfigFromBackend(apiBaseUrl)` — async, fire-and-forget
//      after React mounts. Hits `/platform-config/currency` and overrides if
//      the backend reports a newer value (admin changed currency in the
//      LabelGenic dashboard between visits).
// Mobile parity: currency_service.dart.
let _currencyCode = 'USD'
let _currencyLocale = 'en-US'

const CACHE_KEY_BASE = 'cache_vendor_currency_v1'

export const BUSINESS_LOCALE = 'en-PK'

/** Get the currently configured currency code (e.g. 'USD', 'PKR'). */
export function getBusinessCurrency(): string {
  return _currencyCode
}

/** Get the currently configured currency locale (e.g. 'en-US', 'en-PK'). */
export function getBusinessLocale(): string {
  return _currencyLocale
}

/**
 * @deprecated The value is captured at module load and never updates after
 * `setPlatformCurrency()` runs. Use `getBusinessCurrency()` instead, or pass
 * an explicit `currency` arg to `formatCurrency()`. Kept exported only so
 * older imports don't break the build.
 */
export const BUSINESS_CURRENCY = _currencyCode

/** Boot-time setter — call once after `/platform-config/currency` resolves. */
export function setPlatformCurrency(config: { code?: string; locale?: string }): void {
  if (config.code) _currencyCode = config.code
  if (config.locale) _currencyLocale = config.locale
}

/** Per-tenant cache key so visiting tenants with different currencies in the
 *  same browser doesn't serve the wrong cached entry to the next tenant. */
function cacheKey(): string {
  const tid = typeof window !== 'undefined'
    ? (window as unknown as { __TENANT_ID__?: string }).__TENANT_ID__
    : undefined
  return tid ? `${CACHE_KEY_BASE}:${tid}` : CACHE_KEY_BASE
}

function readTenantLocalization(): { currency?: string; locale?: string; language?: string } {
  if (typeof window === 'undefined') return {}
  const tc = (window as unknown as {
    __TENANT_CONFIG__?: {
      branding?: { localization?: { currency?: string; locale?: string; language?: string } }
    }
  }).__TENANT_CONFIG__
  return tc?.branding?.localization ?? {}
}

function normalizeLocale(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null
  try {
    return Intl.getCanonicalLocales(value.trim())[0] ?? null
  } catch {
    return null
  }
}

/**
 * Synchronous seed run before `createRoot`. Reads the tenant currency from
 * the middleware-inlined `__TENANT_CONFIG__` (or a previously-cached value
 * from this tenant) and applies it to the module-level state so the first
 * paint shows the correct symbol — no waiting on the network.
 *
 * Safe for Cravo + tenants without a configured currency: falls through to
 * 'USD' / 'en-US' (the existing defaults), which matches pre-feature
 * behavior so nothing regresses.
 */
export function initCurrencyConfig(): void {
  // 1. Tenant config (inlined by middleware on prod, by Vite plugin on dev).
  const localization = readTenantLocalization()
  if (typeof localization.currency === 'string' && localization.currency) {
    _currencyCode = localization.currency
  }
  const tenantLocale = normalizeLocale(localization.locale) ?? normalizeLocale(localization.language)
  if (tenantLocale) {
    _currencyLocale = tenantLocale
  }

  // 2. localStorage cache (prior backend fetch). Only applied if the tenant
  //    config didn't set the code — admin changes the tenant config first,
  //    cache is just a fast path for the second visit of the same tenant.
  if (typeof window === 'undefined') return
  try {
    const cached = window.localStorage.getItem(cacheKey())
    if (!cached) return
    const parsed = JSON.parse(cached) as { code?: string; locale?: string }
    if (!localization.currency && typeof parsed.code === 'string' && parsed.code) {
      _currencyCode = parsed.code
    }
    if (!tenantLocale && typeof parsed.locale === 'string' && parsed.locale) {
      const normalized = normalizeLocale(parsed.locale)
      if (normalized) _currencyLocale = normalized
    }
  } catch {
    /* storage blocked — fall through with whatever we already have */
  }
}

/**
 * Fire-and-forget post-mount refresh.
 *
 * Tenant config wins — when `branding.localization.currency` is set by the
 * LabelGenic admin, that's the authoritative choice for this tenant and we
 * do NOT let the backend's platform-wide `/platform-config/currency` (which
 * defaults to USD across the whole platform) override it. We still write
 * the resolved value to the per-tenant localStorage cache so the next
 * cold-start of the same browser/tenant skips the inline-config parse.
 *
 * For Cravo standalone (no tenant config injected) the backend platform
 * setting becomes the source of truth — same behavior as before.
 *
 * Tolerates network failures silently; the seed from `initCurrencyConfig()`
 * keeps the UI usable.
 */
export async function refreshCurrencyConfigFromBackend(apiBaseUrl: string): Promise<void> {
  if (typeof window === 'undefined') return

  const tenantCurrency = readTenantLocalization().currency
  if (typeof tenantCurrency === 'string' && tenantCurrency) {
    // Tenant explicitly chose this currency — backend platform default
    // (USD) must not override. Just persist the seeded value for next time.
    try {
      window.localStorage.setItem(
        cacheKey(),
        JSON.stringify({ code: _currencyCode, locale: _currencyLocale }),
      )
    } catch {
      /* storage blocked — best-effort */
    }
    return
  }

  try {
    const url = `${apiBaseUrl.replace(/\/+$/, '')}/platform-config/currency`
    const res = await fetch(url, { credentials: 'omit' })
    if (!res.ok) return
    const data = (await res.json()) as { code?: unknown; locale?: unknown }
    const code = typeof data.code === 'string' && data.code ? data.code : null
    const locale = normalizeLocale(data.locale)
    if (code) _currencyCode = code
    if (locale) _currencyLocale = locale
    try {
      window.localStorage.setItem(
        cacheKey(),
        JSON.stringify({ code: _currencyCode, locale: _currencyLocale }),
      )
    } catch {
      /* storage blocked — best-effort */
    }
  } catch {
    /* network down or backend unreachable — UI keeps the seeded value */
  }
}

export function formatCurrency(amount: number, currency?: string): string {
  return new Intl.NumberFormat(_currencyLocale, {
    style: 'currency',
    currency: currency ?? _currencyCode,
    minimumFractionDigits: 0,
  }).format(amount)
}

export function formatDate(date: string | Date): string {
  const tz = getTenantTimezone()
  return tz ? dayjs(date).tz(tz).format('MMM D, YYYY') : dayjs(date).format('MMM D, YYYY')
}

export function formatTime(date: string | Date): string {
  const tz = getTenantTimezone()
  return tz ? dayjs(date).tz(tz).format('h:mm A') : dayjs(date).format('h:mm A')
}
